import { HordeModel, HordeWorker } from '../../common/adapters'
import { AppSchema } from '../../common/types/schema'
import { EVENTS, events } from '../emitter'
import { setAssetPrefix, storage } from '../shared/util'
import { api, getUserId } from './api'
import { createStore, getStore } from './create'
import { InitEntities, usersApi } from './data/user'
import { toastStore } from './toasts'
import { subscribe } from './socket'
import { ReplicateModel } from '/common/types/replicate'
import { getSubscriptionModelLimits, getUserType, tryParse, wait } from '/common/util'
import { setContextLimitStrategy } from '/common/prompt'
import { filterImageModels } from '/common/image-util'
import type { FeatherlessModel } from '/srv/adapter/featherless'
import type { ArliModel } from '/srv/adapter/arli'
import { debug } from '/common/debug'

export type SettingState = {
  guestAccessAllowed: boolean
  initLoading: boolean
  cfg: {
    loading: boolean
    ttl: number
  }

  config: AppSchema.AppConfig

  allImageModels: AppSchema.ImageModel[]
  models: HordeModel[]
  workers: HordeWorker[]
  imageWorkers: HordeWorker[]
  anonymize: boolean

  init?: {
    profile: AppSchema.Profile
    user: AppSchema.User
    presets: AppSchema.UserGenPreset[]
    config: AppSchema.AppConfig
    books: AppSchema.MemoryBook[]
  }

  loras: Array<{ id: string; name: string; tags: Record<string, number> }>
  embeddings: Array<{ id: string; name: string; tags: string[] }>

  replicate: Record<string, ReplicateModel>
  featherless: { models: FeatherlessModel[]; classes: Record<string, { ctx: number; res: number }> }
  arliai: { models: ArliModel[]; classes: Record<string, { ctx: number; res: number }> }

  slotsLoaded: boolean
  slots: { publisherId: string; provider?: 'google' | 'ez' | 'fuse' } & Record<string, any>
}

const HORDE_URL = `https://aihorde.net/api/v2`

const initState: SettingState = {
  anonymize: JSON.parse(storage.localGetItem('agnai-anonymize') || 'false'),
  guestAccessAllowed: canUseStorage(),
  initLoading: true,
  cfg: { loading: false, ttl: 0 },
  models: [],
  workers: [],
  imageWorkers: [],
  allImageModels: [],
  loras: [],
  embeddings: [],
  config: {
    serverConfig: {} as any,
    registered: [],
    adapters: [],
    canAuth: true,
    version: '...',
    assetPrefix: '',
    selfhosting: false,
    imagesSaved: false,
    pipelineProxyEnabled: false,
    authUrls: [],
    horde: { workers: [], models: [] },
    openRouter: { models: [] },
    subs: [],
  },
  replicate: {},
  featherless: { models: [], classes: {} },
  arliai: { models: [], classes: {} },

  slotsLoaded: false,
  slots: { publisherId: '' },
}

export const settingStore = createStore<SettingState>(
  'settings',
  initState
)((get, setter) => {
  events.on(EVENTS.loggedOut, () => {
    const prev = get()
    settingStore.setState({ ...initState, slots: prev.slots, slotsLoaded: prev.slotsLoaded })
    settingStore.init()
  })

  events.on(EVENTS.loggedIn, () => {
    const prev = get()
    settingStore.setState({ ...initState, slots: prev.slots, slotsLoaded: prev.slotsLoaded })
    settingStore.init()
  })

  events.on(EVENTS.configUpdated, (config) => {
    const prev = get().config
    settingStore.setState({ config: { ...prev, serverConfig: config } })
  })

  events.on('checkout-success', () => {
    settingStore.getConfig()
  })

  return {
    async *init({ config: prev }) {
      yield { initLoading: true }

      const caches = await loadUserCachedEntities()
      const res = await usersApi.getInit(caches)

      if (res.result) {
        const init = res.result as InitEntities
        const userId = getUserId()

        if (caches?.books) {
          getStore('memory').setState({
            books: { list: caches.books.filter((item) => item.userId === userId), loaded: true },
          })
        }

        if (caches?.presets) {
          getStore('presets').setState({
            presets: caches.presets
              .filter((item) => item.userId === userId)
              .map((preset) => {
                if (!preset.thirdPartyKey) return preset
                preset.userThirdPartyKey = preset.thirdPartyKey
                preset.thirdPartyKey = ''
                return preset
              }),
            presetsLoaded: true,
          })
        }

        if (caches?.allChars) {
          const allChars = Array.isArray(caches.allChars)
            ? caches.allChars
            : Array.isArray((caches.allChars as any)?.list)
            ? (caches.allChars as any).list
            : []

          getStore('character').receiveCharacterList(allChars)
        }

        if (caches?.allChats) {
          getStore('chat').setState({
            allChats: caches.allChats.filter((item) => item.userId === userId).sort(sortDesc),
          })
        }

        if (init.user) {
          init.user.userHordeKey = init.user.hordeKey
          init.user.hordeKey = ''
          getStore('user').setState({
            user: init.user,
            profile: init.profile,
            userType: getUserType(init.user),
          })
        }

        setAssetPrefix(init.config.assetPrefix)
        loadSlotConfig(init.config?.serverConfig?.slots)

        const isMaint = init.config?.maintenance

        if (init.config.serverConfig) {
          yield { allImageModels: init.config.serverConfig.imagesModels || [] }

          init.config.serverConfig.imagesModels = filterImageModels(
            init.user,
            init.config.serverConfig.imagesModels,
            init.config.tier
          )
        }

        if (!isMaint) {
          events.emit(EVENTS.init, init)
        }

        yield {
          init,
          config: init.config,
          replicate: init.replicate || {},
          initLoading: false,
        }

        if (!isMaint) {
          handlePostInit(init, caches)
        }

        const maint = init.config?.maintenance

        if (!maint && prev.maintenance) {
          toastStore.success(`Agnaistic is no longer in maintenance mode`, 10)
        }

        if (maint && !prev.maintenance) {
          toastStore.warn(`Agnaistic is in maintenance mode`, 20)
        }
      }

      if (res.error) {
        if (res.status === 401) {
          toastStore.info(`Your session has expired. Please login again.`)
          getStore('user').logout()
          return
        }

        if (res.status >= 400) {
          toastStore.error(`Could not get settings from server.`)
          yield { initLoading: false }
          return
        }

        setTimeout(() => settingStore.init(), 2500)
      }
    },

    async getFeatherless(state) {
      if (state.featherless.models.length) return

      const res = await api.get('/settings/featherless')

      if (res.result?.models?.length) {
        return { featherless: res.result }
      }
    },
    async getArliAI(state) {
      if (state.arliai.models.length) return

      const res = await api.get('/settings/arli')

      if (res.result?.models?.length) {
        return { arliai: res.result }
      }
    },
    async *getServerConfig({ cfg, config, init }) {
      if (cfg.loading) return

      yield { cfg: { loading: true, ttl: cfg.ttl } }
      const res = await api.get<AppSchema.AppConfig>('/settings')
      yield { cfg: { loading: false, ttl: cfg.ttl } }

      const serverConfig = res.result?.serverConfig
      if (serverConfig) {
        serverConfig.imagesModels = filterImageModels(
          init?.user!,
          serverConfig.imagesModels,
          res.result?.tier
        )
        return { config: { ...config, serverConfig } }
      }
    },
    async *getImageLoras() {
      const res = await api.get('/settings/image-loras')
      if (res.result) {
        return {
          loras: res.result.loras,
          embeddings: res.result.embeddings,
        }
      }
    },
    async *getConfig({ cfg }) {
      if (cfg.loading) return
      if (Date.now() - cfg.ttl < 60000) return

      yield { cfg: { loading: true, ttl: Date.now() } }
      const res = await api.get('/settings')
      yield { cfg: { loading: false, ttl: Date.now() } }

      if (res.result) {
        return { config: res.result }
      }
    },
    async getHordeModels() {
      const res = await api.get<{ models: HordeModel[] }>('/horde/models')
      if (res.result) {
        return { models: res.result.models }
      }
    },
    async getHordeWorkers() {
      try {
        const res = await fetch(`${HORDE_URL}/workers?type=text`)
        const json = await res.json()

        return { workers: json }
      } catch (ex) {
        toastStore.error(`Could not retrieve Horde workers`)
        console.error(ex)
      }
    },

    async getHordeImageWorkers() {
      try {
        const res = await fetch(`${HORDE_URL}/workers?type=image`)
        const json = await res.json()

        return { imageWorkers: json }
      } catch (ex) {
        toastStore.error(`Could not retrieve Horde workers`)
        console.error(ex)
      }
    },

    toggleAnonymize({ anonymize }) {
      storage.localSetItem('agnai-anonymize', JSON.stringify(!anonymize))
      return { anonymize: !anonymize }
    },
  }
})

setContextLimitStrategy((user, gen) => {
  const {
    config: { subs },
  } = settingStore.getState()
  const { sub } = getStore('user').getState()
  if (!gen || gen.service !== 'agnaistic') return

  const modelId = gen.providerModels?.agnaistic ?? gen.registered?.agnaistic?.subscriptionId ?? ''
  const tier = subs.find((sub) => sub._id === modelId)
  if (!tier) return

  const level = user.admin ? Infinity : sub?.level ?? -1

  const limits = getSubscriptionModelLimits(tier.preset, level)
  if (!limits) return

  return { context: limits.maxContextLength, tokens: limits.maxTokens }
})

let firstConnection = true
subscribe('connected', { uid: 'string' }, (body) => {
  if (firstConnection) {
    firstConnection = false
    return
  }

  const { initLoading } = settingStore.getState()
  if (initLoading) return

  settingStore.getConfig()
})

function canUseStorage(noThrow?: boolean) {
  const TEST_KEY = '___TEST'
  localStorage.setItem(TEST_KEY, 'ok')
  const value = localStorage.getItem(TEST_KEY)
  localStorage.removeItem(TEST_KEY)

  if (value !== 'ok') {
    if (!noThrow) throw new Error('Failed to retreive set local storage item')
    return false
  }

  return true
}

async function loadSlotConfig(serverSlots?: string) {
  const slots: any = { publisherId: '' }
  const server = serverSlots ? tryParse(serverSlots) || {} : {}

  const useDev = location.host !== 'agnai.chat'

  try {
    const content = await fetch('/slots.txt', { cache: 'no-cache' }).then((res) => res.text())
    const config = tryParse(content) || {}

    for (const [prop, value] of Object.entries(config)) {
      const key = prop as keyof typeof slots
      slots[key] = value
    }

    const devInject = useDev ? server?.dev_inject : undefined
    const devProvider = useDev ? server?.dev_provider : undefined
    const inject = devInject || server.inject || config.inject

    server.provider = devProvider || server.provider || slots.provider

    if (server.provider && inject) {
      await wait(0.2)
      const node = document.createRange().createContextualFragment(inject)
      try {
        document.head.append(node)
      } catch (ex) {}
    }
  } catch (ex: any) {
    console.log(ex.message)
  } finally {
    await wait(0.01)
    settingStore.setState({ slots: Object.assign(slots, server), slotsLoaded: true })
  }
}

subscribe('configuration-update', { configuration: 'any' }, (body) => {
  const { config } = settingStore.getState()
  settingStore.setState({
    config: {
      ...config,
      serverConfig: body.configuration,
    },
  })
})

subscribe('submodel-updated', { model: 'any' }, (body) => {
  const { config } = settingStore.getState()
  const incoming: AppSchema.SubscriptionModelOption = body.model

  const exists = config.subs.find((sub) => sub._id === incoming._id)

  const next = exists
    ? config.subs.map((sub) => (sub._id === incoming._id ? incoming : sub))
    : config.subs.concat(incoming)

  const opts = next.map((sub) => ({ label: sub.name, value: sub._id }))

  const registered = config.registered.map((reg) => {
    if (reg.name !== 'agnaistic') return reg
    const settings = reg.settings.map((s) =>
      s.field === 'subscriptionId' ? { ...s, setting: { ...s.setting, options: opts } } : s
    )
    return { ...reg, settings }
  })

  /** TODO: Check if:
   * - Model already exists
   * - User tier was ineligible
   * - User tier is now eligible
   */

  const { user, userLevel } = getStore('user').getState()
  const wasIneligible = exists && exists.level > userLevel && incoming.level <= userLevel
  if (!exists) {
    const isEligible = incoming.level <= userLevel || !!user?.admin
    if (isEligible) {
      toastStore.success(`A new model has been added: "${incoming.name}"`, 30)
    }
  } else if (wasIneligible) {
    toastStore.success(`A new model is now available to you: "${incoming.name}"`, 30)
  }

  settingStore.setState({ config: { ...config, subs: next, registered } })
})

subscribe(
  'subscription-replaced',
  { subscriptionId: 'string', replacementId: 'string' },
  (body) => {
    const { config } = settingStore.getState()
    const next = config.subs.filter((sub) => sub._id !== body.subscriptionId)

    settingStore.setState({ config: { ...config, subs: next } })
  }
)

async function loadUserCachedEntities() {
  const userId = getUserId()
  if (!userId) return

  const books = await storage.userCacheGet<AppSchema.MemoryBook[]>('books')
  const presets = await storage.userCacheGet<AppSchema.UserGenPreset[]>('presets')
  const templates = await storage.userCacheGet<AppSchema.PromptTemplate[]>('templates')
  const allChats = await storage.userCacheGet<AppSchema.Chat[]>('all-chats')
  const allChars = await storage.userCacheGet<AppSchema.Character[]>('all-chars')

  debug('init')(`%j`, {
    books: !!books,
    presets: !!presets,
    templates: !!templates,
    allChats: !!allChats,
    allChars: !!allChars,
  })

  return { books, templates, presets, allChats, allChars }
}

async function handlePostInit(
  init: InitEntities,
  caches: Awaited<ReturnType<typeof loadUserCachedEntities>>
) {
  if (init.allChars) {
    getStore('character').loadImpersonate()
  }

  await Promise.all([
    caches?.presets ? getStore('presets').getPresets() : Promise.resolve(),
    caches?.books ? await getStore('memory').getAll() : Promise.resolve(),
    getStore('character').getAllChats(true),
  ])

  if (!init.allChars) {
    getStore('character').loadImpersonate()
  }
}

function sortDesc(left: { updatedAt: string }, right: { updatedAt: string }): number {
  return left.updatedAt > right.updatedAt ? -1 : left.updatedAt === right.updatedAt ? 0 : 1
}
